import type { HTMLAttributes, ReactNode } from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { cn } from '../cn.js';
import { Button } from './button.js';

/* ───────────── Status ───────────── */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'muted';

const dotTone: Record<Tone, string> = {
  neutral: 'bg-fg-3',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-brand',
  muted: 'bg-line-strong',
};

/** Indicador de estado: ponto + texto. Nunca depende só da cor. */
export function StatusDot({ tone, children, pulse, className }: { tone: Tone; children?: ReactNode; pulse?: boolean; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-sm text-fg-2', className)}>
      <span className={cn('size-1.5 shrink-0 rounded-full', dotTone[tone], pulse && 'animate-pulse-soft')} aria-hidden="true" />
      {children}
    </span>
  );
}

const badgeTone: Record<Tone, string> = {
  neutral: 'bg-muted text-fg-2',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  danger: 'bg-danger-subtle text-danger',
  info: 'bg-brand-subtle text-brand',
  muted: 'bg-subtle text-fg-3 border border-line',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex h-5 items-center rounded-sm px-1.5 text-xs font-medium whitespace-nowrap', badgeTone[tone], className)}>
      {children}
    </span>
  );
}

/* ───────────── Layout de página ───────────── */

export function PageHeader({
  title,
  description,
  actions,
  meta,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-x-6 gap-y-3', className)}>
      <div className="min-w-0">
        <h1 className="text-xl font-medium tracking-[-0.01em] text-fg">{title}</h1>
        {description && <p className="mt-1 max-w-2xl text-sm text-fg-3">{description}</p>}
        {meta && <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fg-3">{meta}</div>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
  className,
  flush,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Sem padding interno (para tabelas). */
  flush?: boolean;
}) {
  return (
    <section className={cn('min-w-0', className)}>
      {(title || actions) && (
        <div className="mb-3 flex items-end justify-between gap-4">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-medium text-fg">{title}</h2>}
            {description && <p className="mt-0.5 text-xs text-fg-3">{description}</p>}
          </div>
          {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className={cn(!flush && '')}>{children}</div>
    </section>
  );
}

export function Panel({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('rounded-lg border border-line bg-bg', className)} {...props}>
      {children}
    </div>
  );
}

/* ───────────── Pares chave-valor ───────────── */

export function KeyValueList({ items, className, columns = 1 }: { items: { label: ReactNode; value: ReactNode }[]; className?: string; columns?: 1 | 2 }) {
  return (
    <dl className={cn('grid gap-x-8', columns === 2 ? 'sm:grid-cols-2' : 'grid-cols-1', className)}>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[minmax(120px,40%)_1fr] items-baseline gap-4 border-b border-line py-2.5 last:border-b-0 sm:[&:nth-last-child(2)]:border-b-0">
          <dt className="text-xs text-fg-3">{it.label}</dt>
          <dd className="min-w-0 text-sm break-words text-fg">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ───────────── Faixa de indicadores ───────────── */

export interface Metric {
  label: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
}

/** Indicadores em uma única faixa com divisórias — em vez de cartões soltos. */
export function MetricStrip({ metrics, className }: { metrics: Metric[]; className?: string }) {
  return (
    <div className={cn('grid grid-cols-2 overflow-hidden rounded-lg border border-line sm:grid-cols-3 xl:grid-cols-[repeat(var(--n),minmax(0,1fr))]', className)} style={{ ['--n' as string]: metrics.length }}>
      {metrics.map((m, i) => (
        <div key={i} className="-mr-px -mb-px min-w-0 border-r border-b border-line px-4 py-3.5">
          <div className="truncate text-xs text-fg-3">{m.label}</div>
          <div className={cn('tabular mt-1 text-2xl font-semibold tracking-[-0.02em]', m.tone === 'danger' ? 'text-danger' : m.tone === 'warning' ? 'text-warning' : 'text-fg')}>
            {m.value}
          </div>
          {m.detail && <div className="mt-0.5 truncate text-xs text-fg-3">{m.detail}</div>}
        </div>
      ))}
    </div>
  );
}

/* ───────────── Estados ───────────── */

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse-soft rounded-sm bg-muted', className)} aria-hidden="true" />;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
  compact,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
  compact?: boolean;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center', compact ? 'px-4 py-8' : 'px-6 py-14', className)}>
      {icon && <div className="mb-3 text-fg-4 [&_svg]:size-5">{icon}</div>}
      <p className="text-sm font-medium text-fg">{title}</p>
      {description && <p className="mt-1 max-w-sm text-sm text-fg-3">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({
  title = 'Não foi possível carregar',
  error,
  onRetry,
  className,
}: {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : undefined;
  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      <AlertTriangle className="mb-3 size-5 text-danger" aria-hidden="true" />
      <p className="text-sm font-medium text-fg">{title}</p>
      {message && <p className="mt-1 max-w-md text-sm text-fg-3">{message}</p>}
      {onRetry && (
        <Button size="sm" className="mt-4" icon={<RotateCw />} onClick={onRetry}>
          Tentar novamente
        </Button>
      )}
    </div>
  );
}

export function Callout({ tone = 'info', title, children, className, action }: { tone?: Tone; title?: ReactNode; children?: ReactNode; className?: string; action?: ReactNode }) {
  const tones: Record<Tone, string> = {
    info: 'border-brand-line bg-brand-subtle/60',
    warning: 'border-[#f1dcae] bg-warning-subtle',
    danger: 'border-[#f3c9c5] bg-danger-subtle',
    success: 'border-[#c4e6d2] bg-success-subtle',
    neutral: 'border-line bg-subtle',
    muted: 'border-line bg-subtle',
  };
  return (
    <div className={cn('flex items-start gap-3 rounded-lg border px-3.5 py-3', tones[tone], className)}>
      <div className="min-w-0 flex-1 text-sm">
        {title && <p className="font-medium text-fg">{title}</p>}
        {children && <div className={cn('text-fg-2', title && 'mt-0.5')}>{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-sm border border-line bg-bg px-1 font-sans text-2xs text-fg-3">{children}</kbd>;
}

export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('font-mono text-xs text-fg-2', className)}>{children}</span>;
}

/** Barra de uso horizontal, para CPU/RAM/disco/orçamento. */
export function UsageBar({ value, max = 100, warnAt = 75, dangerAt = 90, className, label }: { value: number; max?: number; warnAt?: number; dangerAt?: number; className?: string; label: string }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const color = pct >= dangerAt ? 'bg-danger' : pct >= warnAt ? 'bg-warning' : 'bg-fg-3';
  return (
    <div className={cn('h-1 w-full overflow-hidden rounded-full bg-muted', className)} role="meter" aria-label={label} aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
    </div>
  );
}
